import { useState, useEffect, useCallback, useRef } from 'react';
import { ControlsBar } from './components/ControlsBar';
import { getAIRecordingVoiceIntent } from './utils/voiceCommandIntents';
import type { RecordingModeParam } from './utils/voiceCommandIntents';
import StandardLeftSidebar from './components/StandardLeftSidebar';
import { AdminCenterLeftSidebarClone } from './components/AdminCenterLeftSidebarClone';
// import Sidebar from './components/Sidebar'; // Oculto: reemplazado por StandardLeftSidebar
import VideoLibrary from './components/VideoLibrary';
import { Video, LibraryBig, Clapperboard, Share2, Tv2, Compass, Trash2 } from 'lucide-react';
import RecycleBinPage from './components/RecycleBinPage';
import { useRecycleBin } from './hooks/useRecycleBin';
import ScreenRecorder from './components/ScreenRecorder';
import SharedVideoPage from './components/SharedVideoPage';
import RecordingStudioPage from './pages/RecordingStudioPage';
import MySharesPage from './components/MySharesPage';
import ChannelPage from './components/ChannelPage';
import ChannelDirectory from './components/ChannelDirectory';
import { ChannelProvider } from './context/ChannelContext';
import VideoEditorPage from './pages/VideoEditorPage';
import { useAuth } from './hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { apiClient } from './lib/apiClient';
import { AppTab, usePreferences } from './context/PreferencesContext';
import { ExtensionThemeProvider } from './components/ExtensionThemeProvider';
import { useSchoolLogo } from './hooks/useSchoolLogo';
import { useFontSize } from './hooks/useFontSize';
import { useNarrowLayout, useMobilePortrait } from './hooks/use-narrow-layout';
import './css/ExtensionThemes.css';
import StandardRightSidebar from './components/StandardRightSidebar';
import { notifyAcvIframeModalOpen } from './utils/notifyAcvIframeModalOpen';
import type { VideoEditDraft } from './types/videoEditDraft';
import { readAiRecordingSidebarCollapsed } from './lib/sidebar-state';
import { toast } from './lib/toast';
import { useArchiveAccess, useArchiveAccessPolling } from './hooks/useArchiveAccess';
import { ArchiveBlockedScreen, ArchiveReadOnlyBanner } from './components/ArchiveNotice';

function App() {
  const { preferences } = usePreferences();
  const narrow = useNarrowLayout();
  const isMobilePortrait = useMobilePortrait();
  // Obtener logo de la escuela
  const { logoUrl, schoolName, loading: logoLoading } = useSchoolLogo();
  // Aplicar fontSize desde ACV App
  useFontSize();
  
  const [activeTab, setActiveTab] = useState<AppTab>(preferences.navigation.defaultTab);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  // false while a recording is running (recorder shows only its floating controls).
  const [recorderBlocking, setRecorderBlocking] = useState(true);
  /** Modo de grabación al abrir desde voz (pipeline: "en pantalla completa" / "select area"). Se limpia al cerrar el modal. */
  const [recordingModalInitialMode, setRecordingModalInitialMode] = useState<RecordingModeParam | undefined>(undefined);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [editingVideo, setEditingVideo] = useState<import('./types/video').Video | null>(null);
  const [editorDraftResume, setEditorDraftResume] = useState<{
    draftId: string;
    project: import('./types/videoEditDraft').VideoEditDraftProject;
  } | null>(null);
  const [globalSearch, setGlobalSearch] = useState<string>('');
  // Handle currently open in the channel directory (null = show directory list)
  // Channel open in the directory. `slug` is the channel's own tenant (may differ from the viewer's).
  const [viewChannel, setViewChannel] = useState<{ handle: string; slug: string } | null>(null);
  // Channel token from URL param ?channelToken=xxx (used to open a shared channel on startup)
  const [pendingChannelToken] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('channelToken')
  );
  const [pendingChannelSlug] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('slug')
  );
  const pendingChannelResolved = useRef(false);
  const { user, loading, signInWithEmail } = useAuth();
  const signInAttempted = useRef(false);
  const { t, i18n } = useTranslation();
  const USE_ADMINCENTER_CLONED_SIDEBAR = (import.meta.env.VITE_AIRECORDING_USE_ADMINCENTER_SIDEBAR ?? 'true') !== 'false';

  // Admin Center Archive Mode (the server enforces it; this keeps the screen in step).
  useArchiveAccessPolling(user?.email);
  const archive = useArchiveAccess();
  const readOnly = archive.mode !== 'full';
  const readOnlyNotice = useCallback(() => {
    toast.info(t('archive.read_only_action', "Not available: your school's account is read-only."));
  }, [t]);
  /** Opens the recorder (not while read-only; one already open is never closed). */
  const openRecording = useCallback((mode?: RecordingModeParam) => {
    if (readOnly) return readOnlyNotice();
    setRecordingModalInitialMode(mode);
    setShowRecordingModal(true);
  }, [readOnly, readOnlyNotice]);

  const openEditVideo = useCallback((v: import('./types/video').Video) => {
    if (readOnly) return readOnlyNotice();
    setEditorDraftResume(null);
    setEditingVideo(v);
  }, [readOnly, readOnlyNotice]);

  const closeEditor = useCallback(() => {
    setEditingVideo(null);
    setEditorDraftResume(null);
  }, []);

  const handleNavigate = useCallback((tabId: string) => {
    closeEditor();
    setActiveTab(tabId as AppTab);
  }, [closeEditor]);

  const handleOpenDraft = useCallback(async (draft: VideoEditDraft) => {
    if (!user) return;
    if (readOnly) return readOnlyNotice();
    // The video as it is now (its links are short-lived), else what the draft saved.
    const { data: library } = await apiClient.listVideos().catch(() => ({ data: null }));
    const current = library?.find((x) => x.id === draft.source.id);
    const v: import('./types/video').Video = current ?? {
      id: draft.source.id,
      title: draft.source.title,
      description: draft.source.description ?? '',
      // A direct storage link saved with the draft has expired by now (links are short-lived).
      url: draft.source.streamUrl && /\.blob\.core\.windows\.net\//.test(draft.source.url || '') ? '' : draft.source.url,
      streamUrl: draft.source.streamUrl,
      file_url: draft.source.file_url,
      user_id: user.id,
      created_at: draft.updatedAt,
      duration: draft.source.duration ?? 0,
    };
    setEditorDraftResume({ draftId: draft.id, project: draft.project });
    setEditingVideo(v);
  }, [user, readOnly, readOnlyNotice]);
  
  // Collapsed/expanded — URL `sb` once per load, then module LS (host global is other-origin).
  const [isCollapsed, setIsCollapsed] = useState(() =>
    readAiRecordingSidebarCollapsed(),
  );

  useEffect(() => {
    const onSetState = (e: Event) => {
      const collapsed = (e as CustomEvent<{ collapsed: boolean }>).detail?.collapsed;
      if (typeof collapsed !== 'boolean') return;
      setIsCollapsed(collapsed);
    };
    window.addEventListener('acv-set-left-sidebar-state', onSetState);
    return () => window.removeEventListener('acv-set-left-sidebar-state', onSetState);
  }, []);

  /**
   * Passport access level for AIRecording ("C" | "P" | "L" | null).
   * null = not a Passport user → full menu (current behavior).
   * "P" or "L" → sidebar restricted to channel-directory only.
   * Fetched from ACV-Backend on mount; same endpoint used by ChannelDirectory for channel scope.
   */
  const [passportAccessLevel, setPassportAccessLevel] = useState<string | null>(null);
  // Recycle Bin count for the menu (not for Passport read-only users, who have no videos).
  const { videos: binVideos } = useRecycleBin(!!user && passportAccessLevel !== 'P' && passportAccessLevel !== 'L', user?.email);
  const ACV_BACKEND_URL = import.meta.env.VITE_ACV_BACKEND_API || 'https://acvnns-api-produc.acv.app';

  useEffect(() => {
    const slug = localStorage.getItem('slug');
    const userEmail = localStorage.getItem('userEmail');
    if (!slug || !userEmail) return;

    fetch(
      `${ACV_BACKEND_URL}/api/passport/my-channel-scope?userEmail=${encodeURIComponent(userEmail)}&slug=${encodeURIComponent(slug)}`
    )
      .then((r) => (r.ok ? r.json() : { accessLevel: null }))
      .then((data) => {
        if (data.accessLevel) setPassportAccessLevel(data.accessLevel);
      })
      .catch(() => { /* non-fatal: full menu is the safe fallback */ });
  // Runs once on mount; email/slug are stable after URL-param effect
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When access is restricted (P/L) and the active tab is not channel-directory, redirect.
  useEffect(() => {
    if (passportAccessLevel && passportAccessLevel !== 'C') {
      if (activeTab !== 'channel-directory') {
        setActiveTab('channel-directory');
      }
    }
  }, [passportAccessLevel, activeTab]);

  // 🔁 Bridge para ACV (host): reportar el ancho real del left sidebar para aplicar shift Fuse-like
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      if (!window.parent || window.parent === window) return; // standalone

      const width = isCollapsed ? 64 : 314;
      window.parent.postMessage(
        {
          type: 'acv-leftsidebar-metrics',
          source: 'aiRecording',
          width,
          collapsed: !!isCollapsed,
          ts: Date.now(),
        },
        '*'
      );
    } catch (e) {
      // ignore
    }
  }, [isCollapsed]);

  // Host ACV: modal de grabación / overlay a pantalla completa → franja sobre navbar + z-index iframe (ACVLayout + App.css)
  // While recording, the recorder doesn't cover the page (the user may record and use this
  // very tab), so ACV must not veil its navbar either.
  // Width taken by the left sidebar, so overlays (BodyPortal) can cover only the content
  // area instead of sitting under / over the sidebar.
  useEffect(() => {
    const width = USE_ADMINCENTER_CLONED_SIDEBAR && isMobilePortrait ? 0 : isCollapsed ? 64 : 314;
    document.documentElement.style.setProperty('--airec-sidebar-width', `${width}px`);
  }, [isCollapsed, isMobilePortrait, USE_ADMINCENTER_CLONED_SIDEBAR]);

  useEffect(() => {
    // However the recorder was closed (button, voice command), the next one starts blocking.
    if (!showRecordingModal) setRecorderBlocking(true);
  }, [showRecordingModal]);

  useEffect(() => {
    const sidebarW = isCollapsed ? 64 : 314;
    notifyAcvIframeModalOpen(showRecordingModal && recorderBlocking, { sidebarWidth: sidebarW });
  }, [showRecordingModal, recorderBlocking, isCollapsed]);

  // Comandos de voz ACV: mapeo parsed/raw → acción; pipeline de params (ej. "grabar en pantalla completa")
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ parsed?: unknown; raw?: string }>;
      const parsed = ev.detail?.parsed;
      const raw = ev.detail?.raw ?? '';
      const result = getAIRecordingVoiceIntent({ parsed: parsed as Record<string, unknown>, raw });
      if (!result) return;
      const { intent, params } = result;
      switch (intent) {
        case 'startRecording':
          openRecording(params?.recordingMode);
          break;
        case 'stopRecording':
          setShowRecordingModal(false);
          setRecordingModalInitialMode(undefined);
          break;
        case 'showChannels':
          closeEditor();
          setActiveTab('library');
          break;
        case 'openLibrary':
          closeEditor();
          setActiveTab('library');
          break;
        case 'openControlsBar':
          window.dispatchEvent(new CustomEvent('acv-show-controls-bar'));
          break;
        case 'closeSidebar':
          window.dispatchEvent(new CustomEvent('acv-force-left-sidebar-collapsed'));
          break;
        case 'openSidebar':
          window.dispatchEvent(new CustomEvent('acv-force-left-sidebar-expanded'));
          break;
        default:
          break;
      }
    };
    window.addEventListener('acv-voice-command', handler);
    return () => window.removeEventListener('acv-voice-command', handler);
  }, [closeEditor, openRecording]);

  // Handle query parameters and local storage
  useEffect(() => {
    console.log('[App] Initializing app and checking for user parameters');
    const params = new URLSearchParams(window.location.search);
    
    // Handle userEmail - check both 'email' and 'userEmail' parameters for compatibility
    let userEmail = params.get('email') || params.get('userEmail');
    console.log('[App] Email from URL params:', userEmail);
    
    // Handle slug. On a channel share link (?channelToken=...) the slug is the
    // channel's tenant, not the viewer's — it must not replace the viewer's own slug.
    let slug = params.get('channelToken') ? null : params.get('slug');
    console.log('[App] Slug from URL params:', slug);
    
    // Store URL parameters in localStorage if present
    if (userEmail) {
      console.log('[App] Storing email in localStorage:', userEmail);
      localStorage.setItem('userEmail', userEmail);
    } else {
      // Try to get from localStorage if not in URL
      userEmail = localStorage.getItem('userEmail');
      console.log('[App] Email from localStorage:', userEmail);
    }

    if (slug) {
      console.log('[App] Storing slug in localStorage:', slug);
      localStorage.setItem('slug', slug);
    } else {
      // Try to get from localStorage if not in URL
      slug = localStorage.getItem('slug');
      console.log('[App] Slug from localStorage:', slug);
    }
    
    // Handle isFromACV parameter
    const isFromACV = params.get('isFromACV');
    if (isFromACV) {
      console.log('[App] Storing isFromACV in localStorage:', isFromACV);
      localStorage.setItem('isFromACV', isFromACV);
    }
    
    // Set user context in API client if we have an email
    if (userEmail) {
      console.log('[App] Setting user context in API client:', { email: userEmail, slug });
      apiClient.setUserContext(userEmail, slug || undefined);
      
      // Verify the user was set correctly in API client
      const apiUser = apiClient.getUser();
      console.log('[App] Verified API client user after setting:', apiUser);
      
      // Only sign in once — useAuth already creates a temp user from localStorage/URL.
      // The ref prevents a retry loop when signIn fails (e.g. backend unavailable).
      if (!user && !loading && !signInAttempted.current) {
        signInAttempted.current = true;
        console.log('[App] No user in auth context, signing in with email:', userEmail);
        signInWithEmail(userEmail);
      } else if (user) {
        console.log('[App] User already exists in auth context:', { email: user.email, slug: user.slug });
      }
    } else {
      console.log('[App] Cannot set user in API client or sign in: no email available');
    }

    // Handle language
    let lang = params.get('language');
    if (lang) {
      localStorage.setItem('language', lang);
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    } else {
      const storedLang = localStorage.getItem('language');
      if (storedLang && i18n.language !== storedLang) {
        i18n.changeLanguage(storedLang);
      }
    }
  }, [user, loading, signInWithEmail, i18n]);

  useEffect(() => {
    setActiveTab(preferences.navigation.defaultTab);
  }, [preferences.navigation.defaultTab]);

  // Check if we're on a shared video page
  useEffect(() => {
    const path = window.location.pathname;
    const shareMatch = path.match(/^\/share\/(.+)$/);
    if (shareMatch) {
      setShareToken(shareMatch[1]);
    }
  }, []);

  // Opens the channel behind a share link (?channelToken=xxx&slug=yyy). `slug` is the channel's tenant.
  const openChannelLink = useCallback((token: string, channelSlug: string) => {
    // Consume the link: remove it from the URL, so the next notification for the same channel
    // (same URL) is still seen as a navigation — main.tsx skips links equal to the current URL.
    const url = new URL(window.location.href);
    url.searchParams.delete('channelToken');
    url.searchParams.delete('slug');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);

    apiClient.getChannelByToken(token, channelSlug).then((result) => {
      if (result.data?.handle) {
        closeEditor();
        setViewChannel({ handle: result.data.handle, slug: result.data.slug || channelSlug });
        setActiveTab('channel-directory');
      } else {
        // Previously ignored — the viewer saw nothing at all.
        toast.warning(t('channel.access_restricted', "This channel isn't available, or you don't have access to it."));
      }
    });
  }, [closeEditor, t]);

  // Channel link in the URL on first load.
  useEffect(() => {
    if (!pendingChannelToken || pendingChannelResolved.current) return;
    // Wait until we have a slug (from the URL param itself, or from auth context)
    const effectiveSlug = pendingChannelSlug || apiClient.getUser().slug;
    if (!effectiveSlug) return;

    pendingChannelResolved.current = true;
    openChannelLink(pendingChannelToken, effectiveSlug);
  }, [pendingChannelToken, pendingChannelSlug, user, openChannelLink]);

  // Channel link arriving while the app is already open — e.g. a "new video in a channel
  // you follow" notification from the ACV bell (main.tsx pushes the path + popstate).
  useEffect(() => {
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('channelToken');
      const channelSlug = params.get('slug') || apiClient.getUser().slug;
      if (token && channelSlug) openChannelLink(token, channelSlug);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [openChannelLink]);

  const isEmbeddedInAcv =
    typeof window !== 'undefined' && window.parent !== window;

  if (loading) {
    // In ACV embedded mode: render a transparent stable shell instead of null.
    // Returning null causes the iframe content to vanish and reappear on every
    // auth state change, creating visible flashing. The shell keeps the layout
    // stable while ACV's ExternalAppPlaceholder overlays the loading state.
    if (isEmbeddedInAcv) {
      return (
        <ExtensionThemeProvider>
          <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--background, #fff)' }} aria-hidden="true" />
        </ExtensionThemeProvider>
      );
    }
    return (
      <div className="min-h-screen t-page flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="t-muted">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // Show shared video page if we have a share token
  if (shareToken) {
    return <SharedVideoPage shareToken={shareToken} />;
  }

  if (archive.mode === 'blocked') {
    return (
      <ExtensionThemeProvider>
        <ArchiveBlockedScreen />
      </ExtensionThemeProvider>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'for-you':
        return (
          <VideoLibrary
            onOpenRecording={() => openRecording()}
            onEdit={openEditVideo}
            onOpenDraft={handleOpenDraft}
            searchQuery={globalSearch}
          />
        );
      case 'library':
        return (
          <VideoLibrary
            onOpenRecording={() => openRecording()}
            onEdit={openEditVideo}
            onOpenDraft={handleOpenDraft}
            searchQuery={globalSearch}
          />
        );
      case 'recording-studio':
        return <RecordingStudioPage onSelectVideo={openEditVideo} />;
      case 'my-shares':
        return <MySharesPage />;
      case 'my-channel':
        return <ChannelPage onEdit={openEditVideo} myEmail={user?.email} />;
      case 'recycle-bin':
        return <RecycleBinPage />;
      case 'channel-directory':
        if (viewChannel) {
          return (
            <ChannelPage
              handle={viewChannel.handle}
              channelSlug={viewChannel.slug}
              onEdit={openEditVideo}
              myEmail={user?.email}
              onBack={() => setViewChannel(null)}
            />
          );
        }
        return (
          <ChannelDirectory
            myEmail={user?.email}
            onOpenChannel={(handle, slug) => setViewChannel({ handle, slug })}
            onOpenMyChannel={() => setActiveTab('my-channel')}
          />
        );
      default:
        const activeTabLabel = String(activeTab);
        return (
          <div className="flex-1 p-6" style={{ backgroundColor: 'var(--bg-primary, #f9fafb)' }}>
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-center min-h-96">
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary, #111827)' }}>
                    {activeTabLabel.charAt(0).toUpperCase() + activeTabLabel.slice(1).replace('-', ' ')}
                  </h2>
                  <p style={{ color: 'var(--text-secondary, #6b7280)' }}>{t('coming_soon')}</p>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  /** Narrow rail: still show Record CTA (icon-only); desktop collapsed wide hides it (Fuse-like desktop). */
  /**
   * Passport access restriction: "P" (Participación) and "L" (Lectura) users
   * may only access the channel directory. "C" (Completo) or null = full menu.
   */
  const isRestrictedAccess = passportAccessLevel === 'P' || passportAccessLevel === 'L';
  // Count beside "Recycle bin" in the menu.
  const binCount = binVideos?.length ?? 0;
  const binBadge = binCount > 0 ? (
    <span aria-label={t('recycle_bin.count', { count: binCount })} style={{
      minWidth: 20, padding: '1px 7px', borderRadius: 999, fontSize: 11, fontWeight: 600, textAlign: 'center',
      background: 'color-mix(in srgb, currentColor 14%, transparent)',
    }}>{binCount}</span>
  ) : null;

  // Full item list — filtered below when Passport restricts access.
  const allSidebarItems = {
    admincenter: [
      { id: 'recording-studio', label: t('recording_studio.nav_label'), icon: Clapperboard },
      { id: 'library', label: t('my_library'), icon: LibraryBig },
      { id: 'my-shares', label: t('my_shares.nav_label', 'My Shares'), icon: Share2 },
      { id: 'my-channel', label: t('my_channel', 'Mi Canal'), icon: Tv2 },
      { id: 'channel-directory', label: t('channel_directory', 'Canales'), icon: Compass },
      { id: 'recycle-bin', label: t('recycle_bin.title', 'Recycle bin'), icon: Trash2, badge: binBadge },
    ],
    standard: [
      { id: 'recording-studio', label: t('recording_studio.nav_label'), icon: <Clapperboard className="w-5 h-5" /> },
      { id: 'library', label: t('my_library'), icon: <LibraryBig className="w-5 h-5" /> },
      { id: 'my-shares', label: t('my_shares.nav_label', 'My Shares'), icon: <Share2 className="w-5 h-5" /> },
      { id: 'my-channel', label: t('my_channel', 'Mi Canal'), icon: <Tv2 className="w-5 h-5" /> },
      { id: 'channel-directory', label: t('channel_directory', 'Canales'), icon: <Compass className="w-5 h-5" /> },
      { id: 'recycle-bin', label: t('recycle_bin.title', 'Recycle bin'), icon: <Trash2 className="w-5 h-5" />, badge: binBadge },
    ],
  };

  const sidebarItems = isRestrictedAccess
    ? {
        admincenter: allSidebarItems.admincenter.filter((i) => i.id === 'channel-directory'),
        standard: allSidebarItems.standard.filter((i) => i.id === 'channel-directory'),
      }
    : allSidebarItems;

  const showRecordQuickAction = !isRestrictedAccess && (!isCollapsed || narrow);
  const recordQuickAction = (
    <div
      style={{
        padding: narrow && isCollapsed ? '6px 0' : '0 12px 10px 12px',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <button
        type="button"
        onClick={() => openRecording()}
        aria-label={t('record_video', 'Record video')}
        style={{
          width: narrow && isCollapsed ? 44 : 130,
          maxWidth: narrow && isCollapsed ? 44 : 130,
          minHeight: narrow && isCollapsed ? 44 : undefined,
          borderRadius: 6,
          border: 'var(--button-primary-border, 1px solid transparent)',
          padding: narrow && isCollapsed ? 0 : '8px 12px',
          fontWeight: 600,
          fontSize: 14,
          backgroundColor: 'var(--button-primary-bg, var(--primary, #1976d2))',
          color: 'var(--button-primary-foreground, #ffffff)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        {narrow && isCollapsed ? <Video size={20} strokeWidth={2} aria-hidden /> : t('record_video_button')}
      </button>
    </div>
  );

  return (
    <ExtensionThemeProvider>
      {/*
        Fuse-like layout — paridad directa con Communities/App.tsx:
        Layout raíz es flex-row h-screen. Sidebar y columna de contenido son hermanos directos.
        El paddingTop para el offset de la navbar ACV va SOLO en la columna de contenido.
        StandardRightSidebar (position:fixed) va fuera de la fila.
      */}
      <StandardRightSidebar
        title={t('history')}
        defaultOpen={false}
        storageKey="aiRecording:rightSidebarState"
        hideFloatingToggle={true}
      >
        <div style={{ color: 'var(--text-secondary, #6b7280)' }}>
          {t('coming_soon')}
        </div>
      </StandardRightSidebar>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: 'var(--bg-primary, #f9fafb)',
        }}
      >
        {USE_ADMINCENTER_CLONED_SIDEBAR ? (
          <AdminCenterLeftSidebarClone
            moduleName="AI Recording"
            moduleInitials="Re"
            schoolLogoUrl={logoUrl}
            schoolName={schoolName}
            logoLoading={logoLoading}
            activeTab={activeTab}
            onNavigate={handleNavigate}
            isFromACV={true}
            onCollapseChange={setIsCollapsed}
            items={sidebarItems.admincenter}
            quickAction={showRecordQuickAction ? recordQuickAction : null}
          />
        ) : (
          <StandardLeftSidebar
            moduleName="AI Recording"
            moduleInitials="Re"
            schoolLogoUrl={logoUrl}
            schoolName={schoolName}
            activeTab={activeTab}
            onNavigate={handleNavigate}
            isFromACV={true}
          items={sidebarItems.standard}
            storageKey="aiRecording:leftSidebarState"
            defaultCollapsed={isCollapsed}
            collapsable={true}
            onCollapseChange={setIsCollapsed}
            quickAction={showRecordQuickAction ? recordQuickAction : null}
          />
        )}
          {/* Columna de contenido — recibe paddingTop del offset ACV (mismo patrón que <Content> en Communities) */}
          <div
            className="airecording-main-content"
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minWidth: 0,
              // P2/P3 — portrait: sidebar is hidden overlay; content takes full width.
              marginLeft: USE_ADMINCENTER_CLONED_SIDEBAR
                ? (isMobilePortrait ? 0 : (isCollapsed ? '64px' : '314px'))
                : '0px',
              paddingTop: USE_ADMINCENTER_CLONED_SIDEBAR ? '0px' : 'var(--acv-embed-top-offset, 0px)',
              position: 'relative',
              zIndex: 0,
            }}
          >
            <ControlsBar
              searchValue={globalSearch}
              onSearchChange={(value) => {
                setGlobalSearch(value);
                // Searches the library (not while editing: that would close unsaved work).
                if (value.trim() && !editingVideo && activeTab !== 'library' && activeTab !== 'for-you') setActiveTab('library');
              }}
              searchPlaceholder={t('search_placeholder') || 'Global Search'}
            />
            {readOnly && <ArchiveReadOnlyBanner />}
            <div style={{ flex: 1, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {editingVideo ? (
                <VideoEditorPage
                  video={editingVideo}
                  resumeDraftId={editorDraftResume?.draftId ?? null}
                  initialDraftProject={editorDraftResume?.project ?? null}
                  onClose={closeEditor}
                />
              ) : (
                <ChannelProvider>
                  {renderContent()}
                </ChannelProvider>
              )}
            </div>
          </div>
        </div>

      <ScreenRecorder
        isOpen={showRecordingModal}
        onClose={() => {
          setShowRecordingModal(false);
          setRecordingModalInitialMode(undefined);
        }}
        onBlockingChange={setRecorderBlocking}
        initialRecordingMode={recordingModalInitialMode}
      />
    </ExtensionThemeProvider>
  );
}

export default App;